const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface Notification {
  id: string;
  title: string;
  body: string;
  type: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
  category: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
  targetUsers: {
    type: 'all' | 'specific' | 'segment';
    userIds?: string[];
    segment?: 'premium' | 'free' | 'active' | 'inactive';
  };
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  scheduledAt?: string;
  sentAt?: string;
  createdBy: {
    id: string;
    nickname: string;
    email: string;
  };
  stats: {
    totalSent: number;
    totalDelivered: number;
    totalOpened: number;
    totalFailed: number;
  };
  deliveryLog?: Array<{
    userId: string;
    status: 'sent' | 'failed';
    sentAt: string;
    errorMessage?: string;
    platform: string;
    messageIds: string[];
    tokenCount: number;
  }>;
  data: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationStats {
  overview: {
    totalNotifications: number;
    totalSent: number;
    totalDelivered: number;
    totalOpened: number;
    totalFailed: number;
  };
  byType: Array<{
    _id: string;
    count: number;
    sent: number;
    delivered: number;
    opened: number;
  }>;
  recent: Notification[];
}

export interface UserNotificationSettings {
  id: string;
  nickname: string;
  email: string;
  pushNotificationEnabled: boolean;
  basicNotifications: boolean;
  promotionNotifications: boolean;
  notificationCategories: string[];
  quietHours?: {
    enabled: boolean;
    start: string;
    end: string;
  };
  activeTokensCount: number;
}

export interface CreateNotificationData {
  title: string;
  body: string;
  type: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
  category: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
  targetUsers: {
    type: 'all' | 'specific' | 'segment';
    userIds?: string[];
    segment?: 'premium' | 'free' | 'active' | 'inactive';
  };
  scheduledAt?: string;
  data?: Record<string, any>;
}

export interface UpdateNotificationData extends Partial<CreateNotificationData> {}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
}

export interface UserSettingsFilters {
  page?: number;
  limit?: number;
  search?: string;
}

class NotificationService {
  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // 알림 목록 조회
  async getNotifications(filters: NotificationFilters = {}): Promise<{
    notifications: Notification[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);

    const response = await fetch(`${API_BASE_URL}/notifications?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notifications');
    }

    const data = await response.json();
    return data.data;
  }

  // 알림 상세 조회
  async getNotification(id: string): Promise<Notification> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notification');
    }

    const data = await response.json();
    return data.data;
  }

  // 알림 생성
  async createNotification(data: CreateNotificationData): Promise<Notification> {
    const response = await fetch(`${API_BASE_URL}/notifications`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create notification');
    }

    const result = await response.json();
    return result.data;
  }

  // 알림 수정
  async updateNotification(id: string, data: UpdateNotificationData): Promise<Notification> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update notification');
    }

    const result = await response.json();
    return result.data;
  }

  // 알림 즉시 발송
  async sendNotification(id: string): Promise<{
    success: boolean;
    totalSent: number;
    totalDelivered: number;
    totalFailed: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}/send`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send notification');
    }

    const result = await response.json();
    return result.data;
  }

  // 알림 취소
  async cancelNotification(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}/cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to cancel notification');
    }
  }

  // 알림 삭제
  async deleteNotification(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete notification');
    }
  }

  // 알림 통계 조회
  async getNotificationStats(): Promise<NotificationStats> {
    const response = await fetch(`${API_BASE_URL}/notifications/stats/overview`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notification stats');
    }

    const data = await response.json();
    return data.data;
  }

  // 사용자 알림 설정 조회
  async getUserSettings(filters: UserSettingsFilters = {}): Promise<{
    users: UserNotificationSettings[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);

    const response = await fetch(`${API_BASE_URL}/notifications/users/settings?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user settings');
    }

    const data = await response.json();
    return data.data;
  }

  // 사용자 알림 설정 업데이트
  async updateUserSettings(
    userId: string,
    settings: {
      pushNotification?: boolean;
      basicNotifications?: boolean;
      promotionNotifications?: boolean;
      notificationCategories?: string[];
      quietHours?: {
        enabled: boolean;
        start: string;
        end: string;
      };
    }
  ): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/users/${userId}/settings`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update user settings');
    }
  }

  // 테스트 알림 발송
  async sendTestNotification(data: {
    userId?: string;
    title: string;
    body: string;
    type: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
    data?: Record<string, any>;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/notifications/test`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send test notification');
    }

    const result = await response.json();
    return result;
  }
}

export const notificationService = new NotificationService();
